import React from "react";
import { useNavigate } from "react-router-dom";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { PageHero, CTASection } from "@/components/SitePage";
import { useLang } from "@/i18n";
import { BookOpen, FileText, GraduationCap, LifeBuoy, Newspaper, ArrowRight, Download } from "lucide-react";

const RED = "#c00000";
const O = { fontFamily: "Outfit, sans-serif" };

export default function Ressourcen() {
  const navigate = useNavigate();
  const { lang } = useLang();
  const de = lang === "de";
  const t = (d, e) => (de ? d : e);

  const RESOURCES = [
    [BookOpen, "Blog", t("Praxiswissen zu Asset-Management, Flotten und IT-Betrieb.", "Practical know-how on asset management, fleets and IT operations."), t("Alle Artikel", "All articles")],
    [FileText, t("Case Studies", "Case studies"), t("Wie Unternehmen mit FleetArea Kosten senken und Ausfälle vermeiden.", "How companies cut costs and avoid failures with FleetArea."), t("Fallstudien ansehen", "View case studies")],
    [Download, "Whitepapers", t("Tiefgehende Leitfäden zu Rollout, Sicherheit und Integration.", "In-depth guides on rollout, security and integration."), t("Herunterladen", "Download")],
    [GraduationCap, t("Webinare & Schulungen", "Webinars & training"), t("Live-Sessions und Aufzeichnungen für Einsteiger und Profis.", "Live sessions and recordings for beginners and pros."), t("Termine ansehen", "See dates")],
    [LifeBuoy, t("Hilfe-Center", "Help center"), t("Anleitungen, FAQ und Antworten auf häufige Fragen.", "Guides, FAQ and answers to common questions."), t("Zum Hilfe-Center", "Go to help center")],
    [Newspaper, "News", t("Produkt-Updates, Releases und Neuigkeiten von FleetArea.", "Product updates, releases and news from FleetArea."), t("Neuigkeiten lesen", "Read news")],
  ];
  const ARTICLES = [
    [t("Rollout in 30 Tagen: So gelingt der Start", "Rollout in 30 days: how to get started"), t("Leitfaden", "Guide"), "6 Min."],
    [t("Ampel-Status richtig nutzen: online, offline, prüfen", "Using traffic-light status: online, offline, check"), t("Praxis", "Practice"), "4 Min."],
    [t("Retouren clever steuern – vom Lager an den Standort", "Smart returns – from stock to the location"), t("Best Practice", "Best practice"), "7 Min."],
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900" style={{ fontFamily: "Inter, sans-serif" }}>
      <SiteNav />
      <PageHero eyebrow={t("RESSOURCEN", "RESOURCES")} title={t("Wissen, das Ihre", "Knowledge that moves")} highlight={t("Assets bewegt", "your assets")} subtitle={t("Leitfäden, Fallstudien und Antworten – alles, was Sie für den erfolgreichen Betrieb Ihrer Geräteflotte brauchen.", "Guides, case studies and answers – everything you need to run your device fleet successfully.")} />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {RESOURCES.map(([Icon, ti, d, cta], i) => (
            <button key={i} data-testid={`ressource-${i}`} onClick={() => navigate("/kontakt")} className="group text-left flex flex-col bg-white border border-gray-100 rounded-2xl p-7 shadow-sm hover:shadow-xl hover:-translate-y-1 hover:border-[#c00000] transition-all">
              <span className="w-12 h-12 rounded-full flex items-center justify-center mb-5" style={{ background: `${RED}12` }}><Icon className="w-6 h-6" style={{ color: RED }} /></span>
              <h3 className="text-lg font-bold mb-2" style={O}>{ti}</h3>
              <p className="text-sm text-gray-600 leading-relaxed flex-1">{d}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: RED }}>{cta} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></span>
            </button>
          ))}
        </div>
      </section>

      <section className="bg-[#f7f8fa] border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <div className="flex items-end justify-between mb-8">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight" style={O}>{t("Empfohlene Artikel", "Recommended articles")}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {ARTICLES.map(([ti, tag, min], i) => (
              <button key={i} onClick={() => navigate("/kontakt")} className="text-left bg-white border border-gray-100 rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 hover:border-[#c00000] transition-all">
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400"><span className="px-2 py-0.5 rounded-full" style={{ background: `${RED}12`, color: RED }}>{tag}</span> · {min}</div>
                <div className="mt-3 text-base font-bold leading-snug" style={O}>{ti}</div>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: RED }}>{t("Weiterlesen", "Read more")} <ArrowRight className="w-4 h-4" /></span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <CTASection title={t("Nichts Passendes gefunden?", "Didn't find what you need?")} text={t("Unser Team beantwortet Ihre Fragen gern persönlich.", "Our team is happy to answer your questions personally.")} primary={t("Frage stellen", "Ask a question")} />
      <SiteFooter />
    </div>
  );
}
